import dayjs, { Dayjs } from 'dayjs';
import { Utilities } from './utilities';

export interface MsTeamsRawData {
  [userEmail: string]: any[];
}

export const calculateWeekRange = (date: Dayjs) => ({
  startDate: date.startOf('isoWeek'),
  endDate: date.endOf('isoWeek'),
});

export const getTimezoneDetails = (events: any) => {
  let timezoneData = {
    offset: '+5',
    abbreviation: 'LOCAL',
    name: 'Local Time',
    source: 'default',
  };

  try {
    if (events && events.length > 0) {
      const eventWithTimezone = events.find(
        (event: any) => (event.start && event.start.includes('+')) || event.start.includes('-'),
      );

      if (eventWithTimezone) {
        const eventTime = dayjs(eventWithTimezone.start);
        const offsetMinutes = eventTime.utcOffset();
        const offsetHours = offsetMinutes / 60;

        timezoneData = {
          offset: Utilities.formatGMTOffset(offsetHours),
          abbreviation: 'EVENT',
          name: 'Event Timezone',
          source: 'google-events',
        };

        return timezoneData;
      }
    }

    const systemOffset = dayjs().utcOffset() / 60;
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    timezoneData = {
      offset: Utilities.formatGMTOffset(systemOffset),
      abbreviation: 'EVENT',
      name: userTimezone,
      source: 'system',
    };
  } catch (error) {
    console.warn('Error calculating timezone, using default:', error);
  }

  return timezoneData;
};

export const getTimezoneDisplayText = (events: any) => {
  const { offset } = getTimezoneDetails(events);
  return `GMT ${offset}`;
};

export const getFilteredEvents = (allEvents: any[], activeFilters: string[]) => {
  return allEvents.filter((event) => {
    if (activeFilters.includes('Google Meet') && event.type === 'Google') return false;
    if (activeFilters.includes('Zoom') && event.type === 'Zoom') return false;
    if (activeFilters.includes('MSTeams') && Object.keys(event).includes('body')) return false;
    return true;
  });
};

export const validateMsTeamsData = (data: unknown): MsTeamsRawData | null => {
  if (!data || typeof data !== 'object') return null;

  const potentialData = data as MsTeamsRawData;
  const isValid = Object.values(potentialData).every(Array.isArray);

  return isValid ? potentialData : null;
};

export const getExternalCalendarUrl = (provider: string) => {
  const urls: Record<string, string> = {
    'Google Meet': 'https://calendar.google.com',
    'Microsoft Teams': 'https://teams.microsoft.com/_#/calendarv2',
    Zoom: 'https://zoom.us/meeting',
  };
  return urls[provider] || '#';
};

export const getAuthUrl = (name: string): string => {
  const redirectUri = process.env.REACT_APP_REDIRECT;
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  switch (name.toLowerCase()) {
    case 'google meet':
    case 'google':
    case 'google calendar':
      return `https://accounts.google.com/o/oauth2/v2/auth/oauthchooseaccount?access_type=offline&prompt=consent&scope=https%3A%2F%2Fwww.googleapis.com%2Fauth%2Fcalendar%20https%3A%2F%2Fwww.googleapis.com%2Fauth%2Fcalendar.readonly%20https%3A%2F%2Fwww.googleapis.com%2Fauth%2Fuserinfo.email&response_type=code&client_id=${googleClientId}&redirect_uri=${redirectUri}&service=lso&o2v=2&flowName=GeneralOAuthFlow`;

    case 'microsoft teams':
    case 'microsoft':
      // return `https://login.microsoftonline.com/dbbc81d8-5d27-4c7d-86be-a95f37ae7601/oauth2/v2.0/authorize?client_id=de5103b1-fa13-4bd4-bf80-3630c23c345c&response_type=code&redirect_uri=${redirectUri}&scope=https://graph.microsoft.com/.default openid profile offline_access`;
      return `https://login.microsoftonline.com/dbbc81d8-5d27-4c7d-86be-a95f37ae7601/oauth2/v2.0/authorize?client_id=de5103b1-fa13-4bd4-bf80-3630c23c345c&response_type=code&prompt=consent&redirect_uri=${redirectUri}&scope=https://graph.microsoft.com/.default openid profile offline_access`;
    case 'zoom':
      return `https://marketplace.zoom.us/authorize?client_id=vl_uXEAR3qovKQ11JzuNw&response_type=code&redirect_uri=${redirectUri}&_zmp_login_state=8A5xmIR2SUqZLoLVpS9pdw`;

    default:
      return '#';
  }
};
